import React, { useState, useEffect } from "react";
import { Card, Modal, Button } from "react-bootstrap";
import { Field, Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DefaultButton from "../../../../shared/components/DefaultButton";
import { SAVE } from "../../../../shared/constants/Constants";
import { v4 as uuid } from "uuid";


const AddQuestions = ({ values, setFieldValue, isEditMode }) => {
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all"); // "all", "active", "inactive"
  const [submitType, setSubmitType] = useState("");
  const formikRef = React.useRef();
  const [editingQuestion, setEditingQuestion] = useState(null);

  const getQuestionIndex = (question) =>
  values.questions.findIndex(
    (q) => (q.id && q.id === question.id) || (q.tempId && q.tempId === question.tempId)
  );

  //  Validation Schema
  const QuestionSchema = Yup.object().shape({
    name: Yup.string().required("Question is required"),
    answerType: Yup.string().required("Answer Type is required"),
    answerValidation: Yup.string(),
    required: Yup.boolean(),
    is_active: Yup.boolean(),
    order: Yup.number()
          .nullable()
          .when("is_active", {
            is: true,
            then: (schema) => schema.required("Order is required").min(1),
            otherwise: (schema) => schema.nullable(),
          })

  });

  //  Initial Values
  const initialQuestionValues = {
    name: "",
    answerType: "text",
    answerValidation: "",
    required: false,
    is_active: true,
    order: values.questions ? values.questions.length + 1 : 1,    
  };

  //  Save Question  
  const handleSaveQuestion = (formValues, { resetForm }, submitType) => {
      let updatedQuestions = [...(values.questions || [])];

      if (formValues.is_active && !formValues.order) {
        formValues.order = updatedQuestions.filter((q) => q.is_active).length + 1;
      }

      if (editIndex !== null) {
        const oldQuestion = updatedQuestions[editIndex];

        const updatedQuestion = {
          ...oldQuestion,
          ...formValues,
          //  Keep order only if active
          order: formValues.is_active
            ? oldQuestion.order || updatedQuestions.filter((q) => q.is_active).length + 1
            : null,
          ...(isEditMode && { isEdited: true }),
        };

        updatedQuestions[editIndex] = updatedQuestion;
      } else {
        // New question
        updatedQuestions.push({
          tempId: uuid(), // temporary ID for drag & drop
          ...formValues,
          order: formValues.is_active
            ? updatedQuestions.filter((q) => q.is_active).length + 1
            : null,
          ...(isEditMode && { isEdited: false }),
        });
      }

      //  Reassign order for active questions only
      let activeOrder = 1;
      updatedQuestions = updatedQuestions.map((q) => {
        if (q.is_active) {
          return { ...q, order: activeOrder++ };
        }
        return { ...q, order: null };
      });

      //  Save all updated states (including inactive changes)
      setFieldValue("questions", updatedQuestions);
      if (submitType === "save_new") {
        //resetForm();                 // clear fields
        setShowQuestionModal(true);
        return;                      // keep modal open
      }

      resetForm();
      setShowQuestionModal(false);
      setEditingQuestion(null);
      setEditIndex(null);
    };

  //  Delete Question
    const handleDeleteQuestion = (index) => {
      const questionToDelete = values.questions[index];

      // If it’s a new unsaved question (no ID), remove it completely
      if (!questionToDelete.id) {
        const updatedQuestions = values.questions.filter((_, i) => i !== index);

        // Recalculate order for active ones only
        let activeOrder = 1;
        const reOrdered = updatedQuestions.map((q) => {
          if (q.is_active) {
            return { ...q, order: activeOrder++ };
          }
          return { ...q, order: null };
        });

        setFieldValue("questions", reOrdered);
      } else {
        // Existing question — mark inactive
        const updatedQuestions = values.questions.map((q, i) =>
          i === index ? { ...q, is_active: false, order: null } : q
        );

        // Recalculate order for active ones only
        let activeOrder = 1;
        const reOrdered = updatedQuestions.map((q) => {
          if (q.is_active) {
            return { ...q, order: activeOrder++ };
          }
          return q;
        });

        setFieldValue("questions", reOrdered);
      }

      setEditIndex(null);
    };

  //  Edit Question
  const handleEditQuestion = (index) => {
    setEditIndex(index);
    setEditingQuestion({ ...values.questions[index] }); //  clone once
    setShowQuestionModal(true);
  };


  //  Handle Drag End
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (activeFilter !== "active") return;

    // Get ACTIVE questions in current order
    const activeQuestions = values.questions.filter(q => q.is_active);

    const reorderedActive = Array.from(activeQuestions);
    const [moved] = reorderedActive.splice(result.source.index, 1);
    reorderedActive.splice(result.destination.index, 0, moved);

    // Merge back into full list
    let activeIndex = 0;
    const updatedAll = values.questions.map((q) => {
      if (!q.is_active) return q;

      const updated = reorderedActive[activeIndex++];
      return { ...updated };
    });

    // Reassign order ONLY for active questions
    let order = 1;
    const finalQuestions = updatedAll.map((q) =>
      q.is_active ? { ...q,order: order++, ...(isEditMode && q.id && { isEdited: true }), }: { ...q, order: null }
      );
      setFieldValue("questions", finalQuestions);
    };

    // cancel for edit mode
  const handleCancelEdit = () => {
      setEditIndex(null);
      setEditingQuestion(null);

      if (formikRef.current) {
        formikRef.current.resetForm({
          values: initialQuestionValues,
        });
      }
    };


  // Filter questions based on active status
  const getFilteredQuestions = () => {
    if (!values.questions) return [];

    switch (activeFilter) {
      case "active":
        return values.questions.filter((q) => q.is_active === true);
      case "inactive":
        return values.questions.filter((q) => q.is_active === false);
      default:
        return values.questions;
    }
  };

  const filteredQuestions = getFilteredQuestions();

  return (
    <>
      {/* Add / Edit Question Form (INLINE – no modal) */}
        <div className="border rounded p-3 mb-4 bg-light">
          <h5 className="mb-3">
            {/* {editIndex !== null ? "Edit Question" : "Add Question"} */}
          </h5>

          <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={editingQuestion ?? initialQuestionValues}
            validationSchema={QuestionSchema}
            onSubmit={(formValues, formikHelpers) => {
              handleSaveQuestion(formValues, formikHelpers, submitType);
            }}
          >

            {({ values: questionValues, handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                
                {/* Row 1: Question + Answer Validation */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label>
                    Question <span className="text-danger">*</span>
                  </label>
                  <Field
                    name="name"
                    className="form-control"
                    placeholder="Enter your question"
                    //disabled={isEditMode && editIndex !== null}
                  />
                  <div className="text-danger">
                    <ErrorMessage name="name" />
                  </div>
                </div>

                <div className="col-md-6">
                  <label>Answer Validation</label>
                  <Field
                    name="answerValidation"
                    className="form-control"
                    placeholder="Optional"
                  />
                </div>
              </div>

              {/* Row 2: Answer Type + Required + Active + Order */}
              <div className="row mb-3 align-items-end">
                <div className="col-md-6">
                  <label>
                    Answer Type <span className="text-danger">*</span>
                  </label>
                  <Field
                    as="select"
                    name="answerType"
                    className="form-select form-select-sm"
                   // disabled={isEditMode && editIndex !== null}
                  >
                    <option value="text">Text</option>
                    <option value="textarea">Text Area</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="dropdown">Dropdown</option>
                    <option value="radio">Radio</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="email">Email</option>
                    <option value="file">File</option>
                    <option value="url">URL</option>
                    <option value="info">Info</option>
                  </Field>
                  <div className="text-danger">
                    <ErrorMessage name="answerType" />
                  </div>
                </div>

                <div className="col-md-2 d-flex align-items-center">
                  <div className="form-check">
                    <Field
                      type="checkbox"
                      name="required"
                      className="form-check-input"
                      id="requiredCheck"
                    />
                    <label className="form-check-label" htmlFor="requiredCheck">
                      Required
                    </label>
                  </div>
                </div>

                <div className="col-md-2 d-flex align-items-center">
                  <div className="form-check">
                    <Field
                      type="checkbox"
                      name="is_active"
                      className="form-check-input"
                      id="activeCheck"
                    />
                    <label className="form-check-label" htmlFor="activeCheck">
                      Active
                    </label>
                  </div>
                </div>

                <div className="col-md-2">
                  <label>Order</label>
                  <input
                    type="text"
                    value={questionValues.order}
                    readOnly
                    className="form-control"
                  />
                </div>
              </div>                
              </Form>
            )}
          </Formik>
        </div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex gap-2">
    <DefaultButton
      buttonClass="btn btn-secondary default-btn-color"
      buttonTitle={editIndex !== null ? "Update Question" : "Add Question"}
      buttonType="button"
      actionBlock={() => {
        if (formikRef.current) {
          formikRef.current.handleSubmit();
        }
      }}
    />

    {editIndex !== null && (
      <DefaultButton
        buttonTitle="Cancel"
        buttonClass="btn-secondary"
        actionBlock={handleCancelEdit}
      />
    )}
  </div>
        {/* Active/Inactive Filter Toggle Buttons */}
          <div className="btn-group" role="group" aria-label="Question filter">
          <button
            type="button"
            className={`btn btn-sm ${activeFilter === "all" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setActiveFilter("all")}
          >
            All
          </button>
          <button
            type="button"
            className={`btn btn-sm ${activeFilter === "active" ? "btn-success" : "btn-outline-success"}`}
            onClick={() => setActiveFilter("active")}
          >
            Active
          </button>
          <button
            type="button"
            className={`btn btn-sm ${activeFilter === "inactive" ? "btn-danger" : "btn-outline-danger"}`}
            onClick={() => setActiveFilter("inactive")}
          >
            Inactive
          </button>
        </div>
        </div>
        {/* Drag-and-Drop Question List */}
        
            {/* Drag-and-Drop Question List */}
            {filteredQuestions && filteredQuestions.length > 0 && (
              <div className="mt-3">
                {activeFilter === "active" ? (
                  //  Active list — draggable
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="questions">
                      {(provided) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                          {filteredQuestions.map((q, index) => (
                            <Draggable key={q.id ?? q.tempId} draggableId={String(q.id ?? q.tempId)} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className="d-flex justify-content-between align-items-center border rounded p-2 mb-2 bg-light"
                                  style={{
                                    ...provided.draggableProps.style,
                                    userSelect: "none",
                                  }}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <strong>
                                      {index + 1}. {q.name}
                                    </strong>

                                    {/* Answer type */}
                                    <span
                                      className="badge"
                                      style={{
                                        backgroundColor: "rgba(0, 123, 255, 0.15)",
                                        color: "#0d6efd",
                                        borderRadius: "12px",
                                        fontWeight: "600",
                                        padding: "5px 10px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {q.answerType}
                                    </span>

                                    {/* Required badge */}
                                    {q.required && (
                                      <span className="badge badge-required">
                                        Required
                                      </span>
                                    )}

                                    {/* Active badge */}
                                    <span
                                      className="badge"
                                      style={{
                                        backgroundColor: "rgba(40, 167, 69, 0.15)",
                                        color: "#28a745",
                                        borderRadius: "12px",
                                        fontWeight: "600",
                                        padding: "5px 10px",
                                        fontSize: "13px",
                                      }}
                                    >
                                      Active
                                    </span>
                                  </div>

                                  {/* Edit + Delete icons */}
                                  <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                                    <i
                                      className="fas fa-edit"
                                      style={{ color: "#0a7674", cursor: "pointer" }}
                                      onClick={() => handleEditQuestion(getQuestionIndex(q))}
                                      aria-hidden="true"
                                    ></i>

                                    <i
                                      className="fas fa-trash"
                                      style={{
                                        color: "#d62728",
                                        cursor: "pointer",
                                      }}
                                      onClick={() =>
                                        handleDeleteQuestion(getQuestionIndex(q))
                                      }
                                      title="Delete Question"
                                      aria-hidden="true"
                                    ></i>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                ) : (
                  //  Inactive or All — not draggable
                  <div>
                    {filteredQuestions.map((q, index) => (
                      <div
                        key={q.id ?? q.tempId}
                        className="d-flex justify-content-between align-items-center border rounded p-2 mb-2 bg-light"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <strong>
                            {index + 1}. {q.name}
                          </strong>
                          <span
                            className="badge"
                            style={{
                              backgroundColor: "rgba(0, 123, 255, 0.15)",
                              color: "#0d6efd",
                              borderRadius: "12px",
                              fontWeight: "600",
                              padding: "5px 10px",
                              fontSize: "13px",
                            }}
                          >
                            {q.answerType}
                          </span>
                          {q.required && (
                            <span className="badge badge-required">
                              Required
                            </span>
                          )}

                          {/* Active / Inactive badge */}
                          <span
                            className="badge"
                            style={{
                              backgroundColor: q.is_active
                                ? "rgba(40, 167, 69, 0.15)"
                                : "rgba(220, 53, 69, 0.15)",
                              color: q.is_active ? "#28a745" : "#dc3545",
                              borderRadius: "12px",
                              fontWeight: "600",
                              padding: "5px 10px",
                              fontSize: "13px",
                            }}
                          >
                            {q.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>

                        {/* Edit + Delete icons */}
                        <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                          <i
                            className="fas fa-edit"
                            style={{
                              color: "#0a7674",
                              cursor: "pointer",
                            }}
                            onClick={() => handleEditQuestion(getQuestionIndex(q))}
                            aria-hidden="true"
                          ></i>

                          <i
                            className="fas fa-trash"
                            style={{
                              color: q.is_active ? "#d62728" : "#ccc",
                              cursor: q.is_active ? "pointer" : "not-allowed",
                              opacity: q.is_active ? 1 : 0.6,
                            }}
                            onClick={() => {
                              if (q.is_active) {
                                handleDeleteQuestion(getQuestionIndex(q));
                              }
                            }}
                            title={
                              q.is_active
                                ? "Delete Question"
                                : "Delete disabled for inactive questions"
                            }
                            aria-hidden="true"
                          ></i>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
    {/* //   </Card.Body>
    // </Card> */}
    </>
  );
};

export default AddQuestions;
